import { useState } from "react";
import AppShell from "../../components/Shell/AppShell";
import I from "../../components/Icons";
import { common, getLanguage, pick, type Language } from "../../lib/i18n";
import { CurrentUser } from "../../types";

const copy = {
  pl: {
    connError: "Błąd połączenia.", inviteSent: (e: string) => `Zaproszenie wysłane na ${e}`,
    confirmDeleteUser: (e: string) => `Usunąć użytkownika ${e}?`,
    title: "Użytkownicy", subtitle: "Zarządzaj kontami i rolami użytkowników tej instancji.",
    registration: "Rejestracja", openRegistration: "Otwarta rejestracja",
    regOpenHint: "Nowi użytkownicy mogą tworzyć konta samodzielnie.",
    regClosedHint: "Rejestracja wyłączona — nowi użytkownicy muszą otrzymać zaproszenie.",
    inviteUser: "Zaproś użytkownika", role: "Rola", sending: "Wysyłam…", sendInvite: "Wyślij zaproszenie",
    language: "Język", languageHint: "Język zaproszenia i domyślny język konta.",
    accounts: "Konta", usersCount: (n: number) => `${n} użytkowników`,
    generations: "Generacje", joined: "Dołączył", you: "ty",
  },
  en: {
    connError: "Connection error.", inviteSent: (e: string) => `Invitation sent to ${e}`,
    confirmDeleteUser: (e: string) => `Delete user ${e}?`,
    title: "Users", subtitle: "Manage the accounts and roles of this instance's users.",
    registration: "Registration", openRegistration: "Open registration",
    regOpenHint: "New users can create accounts themselves.",
    regClosedHint: "Registration disabled — new users must be invited.",
    inviteUser: "Invite user", role: "Role", sending: "Sending…", sendInvite: "Send invitation",
    language: "Language", languageHint: "Invitation email language and the account's default language.",
    accounts: "Accounts", usersCount: (n: number) => `${n} users`,
    generations: "Generations", joined: "Joined", you: "you",
  },
};

type UserRow = {
  id: number;
  email: string;
  role: string;
  created_at: string;
  generations_count: number;
};

type Props = {
  users: UserRow[];
  current_user: CurrentUser;
  products?: { id: number; name: string }[];
  roles: string[];
  registration_open: boolean;
};

const ROLE_LABELS: Record<string, string> = {
  admin:     "Admin",
  moderator: "Moderator",
  user:      "User",
};

const ROLE_COLORS: Record<string, string> = {
  admin:     "var(--accent-ink)",
  moderator: "var(--ink-2)",
  user:      "var(--muted)",
};

export default function AdminUsers({ users: initial, current_user, products = [], roles, registration_open: initialRegistrationOpen }: Props) {
  const t = pick(copy);
  const c = common();
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const isAdmin = current_user.role === "admin";
  const [users, setUsers] = useState(initial);
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviteLocale, setInviteLocale] = useState<Language>(getLanguage());
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(initialRegistrationOpen);
  const [savingRegistration, setSavingRegistration] = useState(false);

  const csrf = () => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";

  const changeRole = async (user: UserRow, role: string) => {
    setSaving(user.id);
    setError(null);
    try {
      const res = await fetch(`/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
        body: JSON.stringify({ user: { role } }),
      });
      const json = await res.json();
      if (json.ok) {
        setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, role: json.user.role } : u));
      } else {
        setError(json.error);
      }
    } catch {
      setError(t.connError);
    } finally {
      setSaving(null);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError(null);
    setInviteSuccess(null);
    try {
      const res = await fetch("/users/invitation", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
        body: JSON.stringify({ user: { email: inviteEmail.trim(), role: inviteRole, locale: inviteLocale } }),
      });
      const json = await res.json();
      if (json.ok) {
        setInviteSuccess(t.inviteSent(json.email));
        setInviteEmail("");
      } else {
        setError(json.error);
      }
    } catch {
      setError(t.connError);
    } finally {
      setInviting(false);
    }
  };

  const deleteUser = async (user: UserRow) => {
    if (!confirm(t.confirmDeleteUser(user.email))) return;
    setSaving(user.id);
    setError(null);
    try {
      const res = await fetch(`/admin/users/${user.id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf() },
      });
      const json = await res.json();
      if (json.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
      } else {
        setError(json.error);
      }
    } catch {
      setError(t.connError);
    } finally {
      setSaving(null);
    }
  };

  const toggleRegistration = async (value: boolean) => {
    setSavingRegistration(true);
    setError(null);
    try {
      const res = await fetch("/admin/users/registration_settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
        body: JSON.stringify({ registration_open: value }),
      });
      const json = await res.json();
      if (json.ok) {
        setIsRegistrationOpen(json.registration_open);
      } else {
        setError(json.error);
      }
    } catch {
      setError(t.connError);
    } finally {
      setSavingRegistration(false);
    }
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 720 }}>
        <div className="page-hd">
          <div>
            <h2>{t.title}</h2>
            <p>{t.subtitle}</p>
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: 14, padding: "8px 12px", background: "oklch(0.62 0.20 25 / 0.08)", border: "1px solid oklch(0.62 0.20 25 / 0.3)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--danger)" }}>
            {error}
          </div>
        )}

        {isAdmin && (
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.registration}</h3></div>
            <div className="panel-body">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.openRegistration}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {isRegistrationOpen
                      ? t.regOpenHint
                      : t.regClosedHint}
                  </div>
                </div>
                <button
                  role="switch"
                  aria-checked={isRegistrationOpen}
                  disabled={savingRegistration}
                  onClick={() => toggleRegistration(!isRegistrationOpen)}
                  style={{
                    flexShrink: 0,
                    width: 40,
                    height: 22,
                    borderRadius: 11,
                    border: "none",
                    cursor: savingRegistration ? "default" : "pointer",
                    background: isRegistrationOpen ? "var(--accent)" : "var(--line)",
                    position: "relative",
                    transition: "background 0.15s",
                    opacity: savingRegistration ? 0.6 : 1,
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 3,
                      left: isRegistrationOpen ? 21 : 3,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "#fff",
                      transition: "left 0.15s",
                    }}
                  />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-hd"><h3>{t.inviteUser}</h3></div>
          <div className="panel-body">
            {inviteSuccess && (
              <div style={{ marginBottom: 10, padding: "7px 10px", background: "oklch(0.86 0.18 130 / 0.12)", border: "1px solid oklch(0.86 0.18 130 / 0.4)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--accent-ink)" }}>
                {inviteSuccess}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                <label>Email</label>
                <input
                  type="email"
                  className="input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  onKeyDown={(e) => e.key === "Enter" && sendInvite()}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>{t.role}</label>
                <select
                  className="input"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  style={{ width: 110 }}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>{t.language}</label>
                <select
                  className="input"
                  value={inviteLocale}
                  onChange={(e) => setInviteLocale(e.target.value as Language)}
                  style={{ width: 110 }}
                >
                  <option value="pl">Polski</option>
                  <option value="en">English</option>
                </select>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={sendInvite}
                disabled={inviting || !inviteEmail.trim()}
                style={{ height: 30, flexShrink: 0 }}
              >
                {inviting ? t.sending : <>{I.plus} {t.sendInvite}</>}
              </button>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
              {t.languageHint}
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-hd">
            <h3>{t.accounts}</h3>
            <span style={{ fontSize: 11, fontFamily: "monospace", color: "var(--muted)" }}>{t.usersCount(users.length)}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--line)" }}>
                {["Email", t.role, t.generations, t.joined, ""].map((h) => (
                  <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontFamily: "monospace", fontSize: 10, color: "var(--muted-2)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.email === current_user.email;
                const busy   = saving === u.id;
                return (
                  <tr key={u.id} style={{ borderBottom: "1px solid var(--line-2)" }}>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontWeight: 500 }}>{u.email}</span>
                      {isSelf && <span style={{ marginLeft: 8, fontSize: 10, fontFamily: "monospace", color: "var(--muted)", background: "var(--bg-3)", padding: "1px 5px", borderRadius: 3 }}>{t.you}</span>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {isAdmin ? (
                        <select
                          value={u.role}
                          disabled={busy || isSelf}
                          onChange={(e) => changeRole(u, e.target.value)}
                          style={{ fontSize: 12, padding: "3px 7px", borderRadius: 4, border: "1px solid var(--line)", background: "var(--surface)", color: ROLE_COLORS[u.role] ?? "var(--ink)", cursor: isSelf ? "default" : "pointer", fontFamily: "monospace" }}
                        >
                          {roles.map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={{ fontSize: 12, fontFamily: "monospace", color: ROLE_COLORS[u.role] ?? "var(--ink)" }}>
                          {ROLE_LABELS[u.role] ?? u.role}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--ink-2)", fontFamily: "monospace", fontSize: 11, textAlign: "right" }}>
                      {u.generations_count}
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--muted)", fontFamily: "monospace", fontSize: 11 }}>
                      {new Date(u.created_at).toLocaleDateString(getLanguage() === "en" ? "en-GB" : "pl-PL")}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      {isAdmin && !isSelf && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--danger)", fontSize: 11 }}
                          disabled={busy}
                          onClick={() => deleteUser(u)}
                        >
                          {busy ? "…" : c.actions.delete}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
